import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import {
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
} from '@vi-iot/client/apis/operational-data/operational-data.constants';
import {
  ActiveModeCommandsGeneric,
  Feature,
  IsEnabledProperty,
  PropertiesStringValueGeneric,
  RoomProperties,
  RoomsCommands,
} from '@vi-iot/client/apis/operational-data/operational-data.model';
import { DeviceIds } from '@vi-iot/client/models';
import { combineLatest, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { InstallationFeaturesStore } from 'src/app/@store/installation-features/installation-features.store';
import { RoomModeModalComponent } from '../../../room-panel/room-mode-modal/room-mode-modal.component';
import { RoomsControlMode } from '@store';
import { TranslateService } from '@ngx-translate/core';
import { SRCRoomControlModeHelper } from './src-room-control-mode-helper';
import { NO_VALUE } from '@pipes';

@Component({
  selector: 'app-src-room-control-mode',
  templateUrl: './src-room-control-mode.component.html',
  styleUrls: ['./src-room-control-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcRoomControlModeComponent implements OnInit, OnDestroy {
  rooms: SRCRoomControlModeHelper.RoomState[] = [];

  availableControlModes = Object.values(RoomsControlMode);
  selectedControlMode = RoomsControlMode.STANDBY;

  isSettingVisibile: boolean;
  isIndeterminate = false;
  isSelectAllChecked = false;
  isAtleaseOneRoomIsDisabled = false;
  noValue = NO_VALUE;

  private roomIdToRoomControlModeMap = new Map<
    string,
    Observable<
      Feature<
        PropertiesStringValueGeneric<RoomsControlMode>,
        ActiveModeCommandsGeneric<RoomsControlMode>
      >
    >
  >();
  private roomIdToRoomDetailsMap = new Map<
    string,
    Observable<Feature<RoomProperties, RoomsCommands>>
  >();
  private roomNameToControlMap = new Map<string, string>();

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  get isDisableSave(): boolean {
    return !this.rooms.some((room) => room?.isSelected === true);
  }

  constructor(
    protected installationFeaturesStore: InstallationFeaturesStore,
    private translate: TranslateService,
    private dialog: MatDialog,
    private cdf: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.rooms, DeviceIds.RoomControl)
      .pipe(
        tap((allRoomsList: Feature<IsEnabledProperty, RoomsCommands>) => {
          const roomIds = allRoomsList.properties.enabled.value;
          roomIds.forEach((roomId) => {
            this.roomIdToRoomDetailsMap.set(
              `${roomId}`,
              this.installationFeaturesStore.selectInstallationDeviceFeature(
                FeatureNames.roomDetails(roomId),
                DeviceIds.RoomControl
              )
            );

            this.roomIdToRoomControlModeMap.set(
              `${roomId}`,
              this.installationFeaturesStore.selectInstallationDeviceFeature(
                FeatureNames.roomControlMode(roomId),
                DeviceIds.RoomControl
              )
            );

            this.roomNameToControlMap.set(
              FeatureNames.roomDetails(roomId),
              FeatureNames.roomControlMode(roomId)
            );
          });
        }),
        switchMap(() => {
          const roomIdToRoomDetailsMapValues = Array.from(this.roomIdToRoomDetailsMap.values());
          const roomIdToRoomControlModeMapValues = Array.from(
            this.roomIdToRoomControlModeMap.values()
          );

          return combineLatest(roomIdToRoomDetailsMapValues).pipe(
            switchMap((roomsDetails) =>
              combineLatest(roomIdToRoomControlModeMapValues).pipe(
                map((roomControlModes) => ({
                  roomsDetails: roomsDetails.filter(
                    (room) => room?.properties?.actors?.value?.length > 0
                  ),
                  roomControlModes,
                }))
              )
            )
          );
        }),
        map(({ roomsDetails, roomControlModes }) => {
          return {
            roomsDetails: roomsDetails.filter((room) => !!room),
            roomControlModes: roomControlModes.filter((room) => !!room),
          };
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(({ roomsDetails, roomControlModes }) => {
        this.isSettingVisibile = roomControlModes.length !== 0;
        this.rooms = [];
        this.mapRoomNameAndCotrolModeWithFeature(roomsDetails, roomControlModes);
        this.disableRowsBasedOnControlModeAvailability(this.selectedControlMode);
        this.cdf.detectChanges();
      });
  }

  private mapRoomNameAndCotrolModeWithFeature(
    roomsDetails: Feature<RoomProperties, RoomsCommands>[],
    roomsControlMode: Feature<
      PropertiesStringValueGeneric<RoomsControlMode>,
      ActiveModeCommandsGeneric<RoomsControlMode>
    >[]
  ): void {
    [...this.roomNameToControlMap.entries()].forEach(([roomNFeature, roomNControlModeFeature]) => {
      const roomDetails = roomsDetails.find((room) => room?.feature === roomNFeature);
      const roomControlMode = roomsControlMode.find(
        (room) => room?.feature === roomNControlModeFeature
      );
      if (!!roomDetails && !!roomControlMode) {
        this.rooms.push({
          roomDetails,
          roomControlMode,
          isDisabled: false,
          isSelected: false,
        });
      }
    });
  }

  onControlModeChange(controlMode: RoomsControlMode): void {
    this.selectedControlMode = controlMode;
    this.disableRowsBasedOnControlModeAvailability(controlMode);
    this.resetSelectAllCheckbox();
  }

  disableRowsBasedOnControlModeAvailability(controlMode: RoomsControlMode): void {
    this.rooms = this.rooms.map((room) => {
      const isDisabled = !room.roomControlMode.commands.setMode.params.mode.constraints.enum.includes(
        controlMode
      );

      return {
        ...room,
        isDisabled,
        isSelected: false,
      };
    });
    this.isAtleaseOneRoomIsDisabled = this.rooms.some((room) => room.isDisabled);
  }

  onRoomCheckboxChange(event: MatCheckboxChange, room: SRCRoomControlModeHelper.RoomState): void {
    this.rooms = this.rooms.map((r) => (r === room ? { ...r, isSelected: event.checked } : r));
    this.updateSelectAllCheckboxStatus();
  }

  updateSelectAllCheckboxStatus(): void {
    const noOfselectedCheckboxs = this.rooms.filter((room) => room.isSelected && !room.isDisabled)
      .length;

    const noOfNonDisabledRooms = this.rooms.filter((room) => !room.isDisabled).length;

    this.isIndeterminate =
      noOfselectedCheckboxs > 0 && noOfselectedCheckboxs !== noOfNonDisabledRooms;

    this.isSelectAllChecked = noOfselectedCheckboxs === noOfNonDisabledRooms;
  }

  onSelectAllCheckboxChange(event: MatCheckboxChange): void {
    this.rooms = this.rooms.map((r) => {
      return {
        ...r,
        isSelected: event.checked && !r.isDisabled,
      };
    });
  }

  onRowClick(room: {
    roomDetails: Feature<RoomProperties, RoomsCommands>;
    roomControlMode: Feature<
      PropertiesStringValueGeneric<RoomsControlMode>,
      ActiveModeCommandsGeneric<RoomsControlMode>
    >;
    isDisabled: boolean;
    isSelected: boolean;
  }): void {
    if (room.isDisabled) {
      return;
    }
    this.rooms = this.rooms.map((r) => (r === room ? { ...r, isSelected: !r.isSelected } : r));

    this.updateSelectAllCheckboxStatus();
  }

  onSave(): void {
    const successMessage = this.translate.instant(
      '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.LIST.SUCCESS_MESSAGE'
    );
    const errorMessage = this.translate.instant(
      '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.LIST.ERROR_MESSAGE'
    );
    const roomsToUpdatePyload = this.rooms
      .filter((room) => room.isSelected)
      .map((room) => {
        return {
          featureName: room.roomControlMode.feature,
          value: this.selectedControlMode,
          propertyName: FeaturePropertyKey.value,
          paramName: FeaturePropertyKey.mode,
          commandName: FeatureCommands.setMode,
          gatewaySerial: null,
          deviceId: DeviceIds.RoomControl,
          config: {
            successMessage,
            errorMessage,
          },
        };
      });

    this.installationFeaturesStore.updateMultipleFeatures(roomsToUpdatePyload);
    this.onSelectAllCheckboxChange({ checked: false } as MatCheckboxChange);
    this.resetSelectAllCheckbox();
  }

  resetSelectAllCheckbox(): void {
    this.isSelectAllChecked = false;
    this.isIndeterminate = false;
  }

  openRoomModeModal(): void {
    this.dialog.open(RoomModeModalComponent, {
      width: '535px',
      disableClose: true,
      panelClass: 'room-mode-modal',
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
